<script lang="ts">
  import { queryDeep } from '$lib/lib/queryDeep';
  import { t as __t, onLocaleChange } from '$lib/i18n';
  import { tutorialStore } from '$lib/stores/tutorialStore.svelte';
  import { untrack } from 'svelte';

  // Reactivity helper for custom i18n
  let _lcv = $state(0);
  $effect(() => {
    const unsub = onLocaleChange(() => _lcv++);
    return unsub;
  });
  function _(key: string, params?: Record<string, string | number>): string {
    _lcv; // read to register dependency
    return __t(key, params);
  }

  // Reactive state derived from store
  let visible = $derived(tutorialStore.isVisible);
  let currentStep = $derived(tutorialStore.currentStep);
  let currentStepIndex = $derived(tutorialStore.currentStepIndex);
  let totalSteps = $derived(tutorialStore.totalSteps);
  let isLastStep = $derived(tutorialStore.isLastStep);

  // Local placement state
  let tooltipStyle = $state('');
  let tooltipEl = $state<HTMLElement | null>(null);
  let activeRect = $state<{
    top: number;
    left: number;
    width: number;
    height: number;
    bottom: number;
    right: number;
  } | null>(null);

  function updatePosition() {
    tooltipStyle = '';
    activeRect = null;

    if (!currentStep) return;

    if (currentStep.position === 'center' || !currentStep.targetSelector) {
      tooltipStyle = 'top: 50%; left: 50%; transform: translate(-50%, -50%);';
      return;
    }

    const el = queryDeep(currentStep.targetSelector);
    if (!el) {
      console.warn(`Target not found in DOM for selector: ${currentStep.targetSelector}`);
      tooltipStyle = 'top: 50%; left: 50%; transform: translate(-50%, -50%);';
      return;
    }

    const rawRect = el.getBoundingClientRect();
    if (rawRect.width === 0 && rawRect.height === 0) {
      // Hidden element fallback
      tooltipStyle = 'top: 50%; left: 50%; transform: translate(-50%, -50%);';
      return;
    }

    const padding = currentStep.highlightPadding ?? 4;
    activeRect = {
      top: rawRect.top - padding,
      left: rawRect.left - padding,
      right: rawRect.right + padding,
      bottom: rawRect.bottom + padding,
      width: rawRect.width + padding * 2,
      height: rawRect.height + padding * 2,
    };

    const rect = activeRect;
    const margin = 12;

    // Responsive: Mobile placement (Viewport bottom/top card)
    if (window.innerWidth < 640) {
      const targetCenterY = rect.top + rect.height / 2;
      if (targetCenterY > window.innerHeight / 2) {
        // Target is in bottom half, put tooltip card at the top
        tooltipStyle = 'top: calc(20px + env(safe-area-inset-top, 0px)); left: 16px; right: 16px; width: calc(100% - 32px);';
      } else {
        // Target is in top half, put tooltip card at the bottom
        tooltipStyle = 'bottom: calc(20px + env(safe-area-inset-bottom, 0px)); left: 16px; right: 16px; width: calc(100% - 32px);';
      }
      return;
    }

    // Desktop placement calculations
    let topStyle = '';
    let leftStyle = '';
    let transform = '';
    let pos = currentStep.position ?? 'bottom';
    const tooltipHeight = tooltipEl ? tooltipEl.offsetHeight : 250;

    // Smart vertical adjustment if card overlaps window edges
    if (pos === 'bottom' && rect.bottom + tooltipHeight + margin > window.innerHeight && rect.top - tooltipHeight - margin > 0) {
      pos = 'top';
    } else if (pos === 'top' && rect.top - tooltipHeight - margin < 0 && rect.bottom + tooltipHeight + margin < window.innerHeight) {
      pos = 'bottom';
    }

    if (pos === 'bottom') {
      let calculatedTop = rect.bottom + margin;
      // Cap bottom to keep it on screen
      if (calculatedTop + tooltipHeight > window.innerHeight - 16) {
        calculatedTop = window.innerHeight - tooltipHeight - 16;
      }
      if (calculatedTop < 16) calculatedTop = 16;
      topStyle = `top: ${calculatedTop}px`;
      leftStyle = `left: ${rect.left + rect.width / 2}px`;
      transform = 'translateX(-50%)';
    } else if (pos === 'top') {
      let calculatedBottom = window.innerHeight - rect.top + margin;
      // Cap top to keep it on screen
      if (window.innerHeight - calculatedBottom < tooltipHeight + 16) {
        calculatedBottom = window.innerHeight - tooltipHeight - 16;
      }
      if (calculatedBottom < 16) calculatedBottom = 16;
      topStyle = `bottom: ${calculatedBottom}px`;
      leftStyle = `left: ${rect.left + rect.width / 2}px`;
      transform = 'translateX(-50%)';
    } else if (pos === 'left') {
      let calculatedTop = rect.top + rect.height / 2;
      const halfHeight = tooltipHeight / 2;
      if (calculatedTop - halfHeight < 16) {
        calculatedTop = halfHeight + 16;
      } else if (calculatedTop + halfHeight > window.innerHeight - 16) {
        calculatedTop = window.innerHeight - halfHeight - 16;
      }
      topStyle = `top: ${calculatedTop}px`;
      leftStyle = `right: ${window.innerWidth - rect.left + margin}px`;
      transform = 'translateY(-50%)';
    } else if (pos === 'right') {
      let calculatedTop = rect.top + rect.height / 2;
      const halfHeight = tooltipHeight / 2;
      if (calculatedTop - halfHeight < 16) {
        calculatedTop = halfHeight + 16;
      } else if (calculatedTop + halfHeight > window.innerHeight - 16) {
        calculatedTop = window.innerHeight - halfHeight - 16;
      }
      topStyle = `top: ${calculatedTop}px`;
      leftStyle = `left: ${rect.right + margin}px`;
      transform = 'translateY(-50%)';
    }

    // Prevent offscreen clipping horizontally for top/bottom
    if (pos === 'top' || pos === 'bottom') {
      const minLeft = 180;
      const maxLeft = window.innerWidth - 180;
      let calculatedLeft = rect.left + rect.width / 2;
      if (calculatedLeft < minLeft) calculatedLeft = minLeft;
      else if (calculatedLeft > maxLeft) calculatedLeft = maxLeft;
      tooltipStyle = `${topStyle}; left: ${calculatedLeft}px; transform: translateX(-50%);`;
    } else {
      tooltipStyle = `${topStyle}; ${leftStyle}; transform: ${transform};`;
    }
  }

  // Recalculate position on resize/scroll events
  function handleLayoutUpdate() {
    if (visible) updatePosition();
  }

  // Scrim click exit handler
  async function handleScrimClick(e: MouseEvent) {
    e.stopPropagation();
    const cancelText = _('tutorial.cancel_confirm') || 'Are you sure you want to cancel the tutorial?';
    if (window.bulmaConfirm) {
      const confirmed = await window.bulmaConfirm(cancelText);
      if (confirmed) {
        tutorialStore.skip();
      }
    } else {
      if (confirm(cancelText)) {
        tutorialStore.skip();
      }
    }
  }

  // Auto-scroll target element into view on step change
  $effect(() => {
    if (visible && currentStepIndex !== undefined) {
      const step = currentStep;
      if (step && step.targetSelector) {
        const el = queryDeep(step.targetSelector);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  });

  // Bind event listeners using Svelte 5 effects
  $effect(() => {
    if (visible && currentStep) {
      untrack(() => updatePosition());
    }
  });

  $effect(() => {
    window.addEventListener('resize', handleLayoutUpdate);
    window.addEventListener('scroll', handleLayoutUpdate, { capture: true, passive: true });
    return () => {
      window.removeEventListener('resize', handleLayoutUpdate);
      window.removeEventListener('scroll', handleLayoutUpdate, { capture: true });
    };
  });
</script>

{#if visible && currentStep}
  <div class="fixed inset-0 z-[9999] pointer-events-none font-sans select-none">
    <!-- Viewport mask overlay -->
    {#if activeRect}
      {@const r = activeRect}
      <!-- Top Panel -->
      <div onclick={handleScrimClick} class="absolute top-0 left-0 right-0 bg-black/60 pointer-events-auto z-[9998] transition-all duration-300" style="height: {r.top}px;"></div>
      <!-- Bottom Panel -->
      <div onclick={handleScrimClick} class="absolute left-0 right-0 bg-black/60 pointer-events-auto z-[9998] transition-all duration-300" style="top: {r.bottom}px; bottom: 0;"></div>
      <!-- Left Panel -->
      <div onclick={handleScrimClick} class="absolute bg-black/60 pointer-events-auto z-[9998] transition-all duration-300" style="top: {r.top}px; height: {r.height}px; width: {r.left}px; left: 0;"></div>
      <!-- Right Panel -->
      <div onclick={handleScrimClick} class="absolute bg-black/60 pointer-events-auto z-[9998] transition-all duration-300" style="top: {r.top}px; height: {r.height}px; right: 0; left: {r.right}px;"></div>
      <!-- Highlight Focus Ring -->
      <div class="absolute border-2 border-primary-500 shadow-[0_0_0_4px_rgba(247,148,29,0.3)] rounded-xl pointer-events-none transition-all duration-300 z-[10000] box-border"
        style="top: {r.top}px; left: {r.left}px; width: {r.width}px; height: {r.height}px;"></div>
    {:else}
      <!-- Backdrop mask when there is no target element -->
      <div onclick={handleScrimClick} class="absolute inset-0 bg-black/60 pointer-events-auto z-[9998]"></div>
    {/if}

    <!-- Tooltip Card -->
    <div
      bind:this={tooltipEl}
      class="absolute bg-white text-gray-800 rounded-2xl p-5 w-[340px] max-w-[calc(100vw-32px)] shadow-[0_12px_36px_rgba(0,0,0,0.3)] flex flex-col gap-4 transition-[top,left,bottom,right] duration-300 ease-out z-[10001] pointer-events-auto border border-gray-100"
      style={tooltipStyle}
    >
      <!-- Header -->
      <div class="flex justify-between items-center border-b border-gray-100 pb-2">
        <h3 class="m-0 text-base font-bold text-gray-900 tracking-tight">{_(currentStep.titleKey)}</h3>
        <span class="text-xs text-gray-400 font-semibold bg-gray-50 px-2 py-0.5 rounded-full">{currentStepIndex + 1} / {totalSteps}</span>
      </div>

      <!-- Description -->
      <div class="text-sm leading-relaxed text-gray-600 font-medium">
        <p class="m-0">{_(currentStep.contentKey)}</p>
      </div>

      <!-- Footer navigation -->
      <div class="flex justify-between items-center mt-2 pt-1">
        <button
          onclick={() => tutorialStore.skip()}
          class="bg-transparent text-gray-400 px-3 py-1.5 rounded-lg text-sm font-semibold cursor-pointer transition-colors duration-200 hover:text-gray-600 hover:bg-gray-100 border-none outline-none"
        >
          {_('tutorial.action.skip')}
        </button>
        <div class="flex gap-2">
          <button
            onclick={() => tutorialStore.prevStep()}
            disabled={currentStepIndex === 0}
            class="px-4 py-2 rounded-lg text-sm font-bold cursor-pointer transition-all duration-200 border-none outline-none disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            {_('tutorial.action.prev')}
          </button>
          <button
            onclick={() => tutorialStore.nextStep()}
            class="px-4 py-2 rounded-lg text-sm font-bold cursor-pointer transition-all duration-200 border-none outline-none bg-primary-500 text-white hover:bg-primary-600 shadow-sm"
          >
            {isLastStep ? _('tutorial.action.finish') : _('tutorial.action.next')}
          </button>
        </div>
      </div>

      <!-- Preference Opt-out -->
      <div class="border-t border-gray-100 pt-3 mt-1 text-xs">
        <label class="flex items-center gap-2.5 cursor-pointer text-gray-500 font-medium select-none">
          <input
            type="checkbox"
            bind:checked={tutorialStore.dontShowAgain}
            class="rounded border-gray-300 text-primary-500 focus:ring-primary-500 h-4.5 w-4.5"
          />
          {_('tutorial.dont_show')}
        </label>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Local overrides if needed, Tailwind utility classes handle most styling */
</style>
